import { useState } from "react";
import { CheckCircle2, Mail, Pencil, Send as SendIcon, X } from "lucide-react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "../../ui/card";
import { sendPersonMessage } from "../../../lib/copilot-api";
import type { GetProToken } from "../../../hooks/useProToken";
import type { ProDraftMessageRender } from "../../../lib/pro-agent-types";

// draft_message confirm card (scenarios 680–682, plan 188 task 803; renamed
// and repointed onto contact_id, Person Model Completion M3). The model
// NEVER sends (D5): Send below calls
// POST /api/professional/people/:contactId/message (plan 377 M5 — the
// contact-keyed route the manual CRM drawer now also uses) directly with
// draft.contact_id; no profile_id crosses this component.
export type MessageConfirmCardProps = {
  draft: ProDraftMessageRender;
  getToken: GetProToken;
  /** Discard notifies the copilot the pro rejected this draft so it can offer
   *  a different take. The card also drops into a terminal "discarded" state. */
  onDiscard: () => void;
  /** Suppressed variant only — persists the draft as a person note instead of sending. */
  onSaveAsNote?: (input: { subject: string; body: string }) => void;
};

type Status = "idle" | "editing" | "sending" | "sent" | "failed" | "discarded";

export function MessageConfirmCard({
  draft,
  getToken,
  onDiscard,
  onSaveAsNote,
}: MessageConfirmCardProps) {
  const [subject, setSubject] = useState(draft.subject);
  const [body, setBody] = useState(draft.body);
  const [status, setStatus] = useState<Status>("idle");
  const [sentAt, setSentAt] = useState<string | null>(null);

  const suppressed = draft.suppression.suppressed;
  const editing = status === "editing";

  async function handleSend() {
    setStatus("sending");
    try {
      await sendPersonMessage(draft.contact_id, { subject, body }, getToken);
      setSentAt(
        new Date().toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        }),
      );
      setStatus("sent");
    } catch {
      setStatus("failed");
    }
  }

  function handleDiscard() {
    setStatus("discarded");
    // Tell the copilot the pro rejected this draft — it can offer a new angle.
    onDiscard();
  }

  if (status === "sent") {
    // Single, honest confirmation — no "Undo" (the email is already sent and
    // cannot be recalled) and no redundant toast. Muted timestamp; only the
    // check icon carries the success color.
    return (
      <Card
        data-testid="message-confirm-card"
        data-status="sent"
        className="border-success/30 bg-success/5"
      >
        <CardContent className="flex items-center gap-2.5 p-4">
          <CheckCircle2
            size={18}
            className="shrink-0 text-success"
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">
              Message sent to {draft.recipient_name || "your person"}
            </p>
            <p className="truncate text-xs text-fg-muted">{subject}</p>
          </div>
          {sentAt && (
            <span className="shrink-0 text-xs font-medium text-fg-muted">
              {sentAt}
            </span>
          )}
        </CardContent>
      </Card>
    );
  }

  if (status === "discarded") {
    return (
      <Card
        data-testid="message-confirm-card"
        data-status="discarded"
        className="border-border-soft bg-muted/40"
      >
        <CardContent className="flex items-center gap-2.5 p-4">
          <X size={16} className="shrink-0 text-fg-muted" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-fg-muted">Draft discarded</p>
            <p className="truncate text-xs text-fg-muted">{subject}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="message-confirm-card" data-status={status}>
      <CardHeader>
        <div className="flex items-center gap-2">
          <span className="inline-flex size-7 items-center justify-center rounded-full bg-muted text-xs font-semibold">
            {(draft.recipient_name || "?").charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-fg">
              {draft.recipient_name || "Person"}
            </p>
            <p className="truncate text-xs text-fg-muted">
              To: {draft.recipient_email || "no email on file"}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {editing ? (
          <>
            <input
              aria-label="Subject"
              className="w-full rounded-md border border-border bg-transparent px-2.5 py-2 text-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={suppressed}
            />
            <textarea
              aria-label="Message"
              className="min-h-[220px] w-full resize-y rounded-md border border-border bg-transparent px-2.5 py-2 text-sm leading-relaxed"
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={suppressed}
            />
          </>
        ) : (
          <>
            <p
              className={
                suppressed
                  ? "text-sm font-medium text-fg-muted"
                  : "text-sm font-medium text-fg"
              }
            >
              {subject}
            </p>
            <p className="whitespace-pre-wrap text-sm text-fg-muted">{body}</p>
          </>
        )}

        {suppressed ? (
          <p
            data-testid="message-suppressed-warning"
            className="rounded-md bg-warning/10 px-2 py-1.5 text-xs text-warning"
          >
            {draft.recipient_name || "This person"} has opted out — sending is
            disabled. You can still save this as a note.
          </p>
        ) : (
          <p data-testid="message-ok-to-send" className="text-xs text-success">
            OK to send — {draft.recipient_name || "this person"} accepts
            messages.
          </p>
        )}

        {status === "failed" && (
          <p
            role="alert"
            data-testid="message-failed-banner"
            className="rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive"
          >
            {draft.recipient_name || "Person"}&apos;s message didn&apos;t send —
            nothing was sent, your draft is saved.
          </p>
        )}
      </CardContent>
      <CardFooter className="mt-1 flex-col items-stretch gap-2.5 rounded-b-lg border-t border-border-soft bg-muted/40 p-4">
        <div className="flex gap-2">
          {status === "failed" ? (
            <Button size="sm" onClick={handleSend}>
              <SendIcon size={14} aria-hidden />
              Try again
            </Button>
          ) : suppressed ? (
            <Button size="sm" onClick={() => onSaveAsNote?.({ subject, body })}>
              Save as note
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleSend}
              disabled={status === "sending"}
            >
              <SendIcon size={14} aria-hidden />
              {status === "sending" ? "Sending…" : "Send"}
            </Button>
          )}
          {!suppressed && (
            <Button
              size="sm"
              variant={editing ? "default" : "outline"}
              aria-pressed={editing}
              onClick={() => setStatus(editing ? "idle" : "editing")}
            >
              <Pencil size={12} aria-hidden />
              {editing ? "Done" : "Edit"}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={handleDiscard}>
            <X size={14} aria-hidden />
            Discard
          </Button>
        </div>
        <p className="flex items-center gap-1 text-[11px] text-fg-muted">
          <Mail size={11} aria-hidden />
          Sent by Sill on behalf of your agent.
        </p>
      </CardFooter>
    </Card>
  );
}
